// src/components/common/LoadingSpinner.jsx
import React from 'react';

const LoadingSpinner = ({ size = 'md', fullScreen = false, color = 'blue', text = 'Loading...' }) => {
  // Size mappings
  const sizes = {
    sm: {
      container: '20px',
      spinner: '16px',
      borderWidth: '2px',
    },
    md: {
      container: '40px',
      spinner: '32px',
      borderWidth: '3px',
    },
    lg: {
      container: '60px',
      spinner: '48px',
      borderWidth: '4px',
    },
    xl: {
      container: '80px',
      spinner: '64px',
      borderWidth: '4px',
    },
  };

  // Color mappings
  const colors = {
    blue: {
      light: '#e0f2fe',
      primary: '#3b82f6',
      dark: '#1e40af',
    },
    gray: {
      light: '#f3f4f6',
      primary: '#6b7280',
      dark: '#374151',
    },
    green: {
      light: '#dcfce7',
      primary: '#22c55e',
      dark: '#166534',
    },
    red: {
      light: '#fee2e2',
      primary: '#ef4444',
      dark: '#991b1b',
    },
    purple: {
      light: '#f3e8ff',
      primary: '#a855f7',
      dark: '#6b21a8',
    },
  };

  const currentSize = sizes[size] || sizes.md;
  const currentColor = colors[color] || colors.blue;

  const styles = {
    // Full screen overlay
    fullScreenOverlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      backdropFilter: 'blur(4px)',
      animation: 'fadeIn 0.2s ease-out',
    },

    // Spinner container
    spinnerContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '1rem',
    },

    // Main spinner
    spinner: {
      width: currentSize.spinner,
      height: currentSize.spinner,
      border: `${currentSize.borderWidth} solid ${currentColor.light}`,
      borderTopColor: currentColor.primary,
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    },

    // Spinner with text
    spinnerWithText: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.75rem',
    },

    // Loading text
    loadingText: {
      fontSize: size === 'sm' ? '0.75rem' : size === 'lg' ? '1rem' : '0.875rem',
      color: currentColor.dark,
      fontWeight: 500,
      animation: 'pulse 1.5s ease-in-out infinite',
    },

    // Dot animation for text
    dotContainer: {
      display: 'flex',
      gap: '0.25rem',
      marginTop: '0.25rem',
    },

    dot: {
      width: size === 'sm' ? '4px' : '6px',
      height: size === 'sm' ? '4px' : '6px',
      backgroundColor: currentColor.primary,
      borderRadius: '50%',
      animation: 'bounce 1.4s infinite ease-in-out both',
    },

    dot1: {
      animationDelay: '-0.32s',
    },

    dot2: {
      animationDelay: '-0.16s',
    },

    // Skeleton loader
    skeletonContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      width: '100%',
      maxWidth: '300px',
    },

    skeletonLine: {
      height: '1rem',
      backgroundColor: currentColor.light,
      borderRadius: '0.25rem',
      animation: 'pulse 1.5s ease-in-out infinite',
    },

    skeletonCircle: {
      width: '3rem',
      height: '3rem',
      backgroundColor: currentColor.light,
      borderRadius: '50%',
      animation: 'pulse 1.5s ease-in-out infinite',
    },

    // Progress bar
    progressContainer: {
      width: '200px',
      height: '4px',
      backgroundColor: currentColor.light,
      borderRadius: '2px',
      overflow: 'hidden',
    },

    progressBar: {
      height: '100%',
      backgroundColor: currentColor.primary,
      borderRadius: '2px',
      animation: 'progress 1.5s ease-in-out infinite',
    },

    // Wave loader
    waveContainer: {
      display: 'flex',
      gap: '0.25rem',
      height: '2rem',
      alignItems: 'center',
    },

    waveBar: {
      width: '4px',
      height: '1rem',
      backgroundColor: currentColor.primary,
      borderRadius: '2px',
      animation: 'wave 1.2s ease-in-out infinite',
    },

    waveBar1: {
      animationDelay: '0s',
    },

    waveBar2: {
      animationDelay: '0.2s',
    },

    waveBar3: {
      animationDelay: '0.4s',
    },

    waveBar4: {
      animationDelay: '0.6s',
    },

    waveBar5: {
      animationDelay: '0.8s',
    },
  };

  // Simple spinner
  const SimpleSpinner = () => (
    <div style={styles.spinnerContainer}>
      <div style={styles.spinner} />
      {text && <span style={styles.loadingText}>{text}</span>}
    </div>
  );

  // Spinner with animated dots
  const SpinnerWithDots = () => (
    <div style={styles.spinnerWithText}>
      <div style={styles.spinner} />
      <div style={styles.dotContainer}>
        <div style={{ ...styles.dot, ...styles.dot1 }} />
        <div style={{ ...styles.dot, ...styles.dot2 }} />
        <div style={styles.dot} />
      </div>
      <span style={styles.loadingText}>{text}</span>
    </div>
  );

  // Skeleton loader
  const SkeletonLoader = () => (
    <div style={styles.skeletonContainer}>
      <div style={{ ...styles.skeletonCircle, margin: '0 auto' }} />
      <div style={{ ...styles.skeletonLine, width: '80%', margin: '0 auto' }} />
      <div style={{ ...styles.skeletonLine, width: '60%', margin: '0 auto' }} />
      <div style={{ ...styles.skeletonLine, width: '70%', margin: '0 auto' }} />
    </div>
  );

  // Progress bar loader
  const ProgressLoader = () => (
    <div style={styles.spinnerContainer}>
      <div style={styles.progressContainer}>
        <div style={styles.progressBar} />
      </div>
      <span style={styles.loadingText}>{text}</span>
    </div>
  );

  // Wave loader
  const WaveLoader = () => (
    <div style={styles.spinnerContainer}>
      <div style={styles.waveContainer}>
        <div style={{ ...styles.waveBar, ...styles.waveBar1 }} />
        <div style={{ ...styles.waveBar, ...styles.waveBar2 }} />
        <div style={{ ...styles.waveBar, ...styles.waveBar3 }} />
        <div style={{ ...styles.waveBar, ...styles.waveBar4 }} />
        <div style={{ ...styles.waveBar, ...styles.waveBar5 }} />
      </div>
      <span style={styles.loadingText}>{text}</span>
    </div>
  );

  // Choose loader type based on props or default to simple spinner
  const getLoader = () => {
    // You can add a `type` prop to switch between different loaders
    // For now, we'll use the simple spinner
    return <SimpleSpinner />;
  };

  const spinner = getLoader();

  if (fullScreen) {
    return (
      <div style={styles.fullScreenOverlay}>
        {spinner}
        
        {/* Add keyframe animations */}
        <style jsx>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
          
          @keyframes bounce {
            0%, 80%, 100% {
              transform: scale(0);
            }
            40% {
              transform: scale(1);
            }
          }
          
          @keyframes progress {
            0% {
              transform: translateX(-100%);
            }
            50% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(100%);
            }
          }
          
          @keyframes wave {
            0%, 40%, 100% {
              transform: scaleY(0.4);
            }
            20% {
              transform: scaleY(1);
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      {spinner}
      
      {/* Add keyframe animations */}
      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }
        
        @keyframes progress {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        @keyframes wave {
          0%, 40%, 100% {
            transform: scaleY(0.4);
          }
          20% {
            transform: scaleY(1);
          }
        }
      `}</style>
    </>
  );
};

// Export different loader types as static properties
// LoadingSpinner.Simple = SimpleSpinner;
// LoadingSpinner.WithDots = SpinnerWithDots;
// LoadingSpinner.Skeleton = SkeletonLoader;
// LoadingSpinner.Progress = ProgressLoader;
// LoadingSpinner.Wave = WaveLoader;

export default LoadingSpinner;